import { useState, useEffect } from 'react';
import {
  Webhook,
  Plus,
  Search,
  Edit,
  Trash2,
  Power,
  PowerOff,
  ExternalLink,
  Copy,
  Check,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Send,
  Eye,
  RotateCcw,
  X,
  KeyRound,
} from 'lucide-react';
import {
  integrationService,
  Webhook as WebhookType,
  WebhookDelivery,
  WebhookEvent,
  WebhookEventType,
  DeliveryStatus,
  CreateWebhookDto,
  UpdateWebhookDto,
  FALLBACK_WEBHOOK_EVENT_TYPES,
  partitionWebhookEvents,
} from '@services/integration.service';
import { useToast } from '@centraoneplus/ui';
import { PasswordInput } from '@components/PasswordInput';

// Example payloads (standard envelope) for the events integration-hub sends.
// Shapes follow developer-portal/docs/webhooks/events.md. An event without an
// entry here simply shows no example.
const ENVELOPE = { api_version: '1.0' };
// Every customer.* payload carries who the customer is (customer-service customer-events.ts).
const EXAMPLE_CUSTOMER = {
  customerId: '770e8400-e29b-41d4-a716-446655440002',
  externalId: 'CRM-2231',
  email: 'customer@example.com',
  name: 'Jane Smith',
  phone: null,
  status: 'active',
  organizationId: null,
  managedBy: null,
};
const TICKET_REF = {
  ticketId: '660e8400-e29b-41d4-a716-446655440001',
  ticketNumber: 'TKT-0042',
  subject: 'Unable to login to dashboard',
  customerId: '770e8400-e29b-41d4-a716-446655440002',
  origin: 'email',
  originName: 'Email',
  originType: 'channel',
  externalSource: null,
  externalId: null,
};

const examplePayloads: Record<string, Record<string, any>> = {
  'ticket.created': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440000',
    type: 'ticket.created',
    ...ENVELOPE,
    created_at: '2026-03-04T12:34:56.789Z',
    data: {
      ticketNumber: 'TKT-0042',
      subject: 'Unable to login to dashboard',
      channel: 'EMAIL',
      priority: 'HIGH',
      customerId: '770e8400-e29b-41d4-a716-446655440002',
      entity: { id: '660e8400-e29b-41d4-a716-446655440001', origin: 'email', '…': '…' },
    },
  },
  'ticket.updated': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440003',
    type: 'ticket.updated',
    ...ENVELOPE,
    created_at: '2026-03-04T12:35:00.000Z',
    data: {
      ticketId: '660e8400-e29b-41d4-a716-446655440001',
      changes: { priority: { from: 'MEDIUM', to: 'HIGH' } },
    },
  },
  'ticket.assigned': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440005',
    type: 'ticket.assigned',
    ...ENVELOPE,
    created_at: '2026-03-04T12:36:00.000Z',
    data: {
      ticketId: '660e8400-e29b-41d4-a716-446655440001',
      previousAssigneeId: null,
      newAssigneeId: '880e8400-e29b-41d4-a716-446655440003',
      previousTeamId: null,
      newTeamId: '990e8400-e29b-41d4-a716-446655440004',
    },
  },
  'ticket.status_changed': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440012',
    type: 'ticket.status_changed',
    ...ENVELOPE,
    created_at: '2026-03-04T13:00:00.000Z',
    data: {
      ticketId: '660e8400-e29b-41d4-a716-446655440001',
      previousStatus: 'OPEN',
      newStatus: 'PENDING',
    },
  },
  'ticket.resolved': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440013',
    type: 'ticket.resolved',
    ...ENVELOPE,
    created_at: '2026-03-04T13:30:00.000Z',
    data: {
      ...TICKET_REF,
      previousStatus: 'PENDING',
      newStatus: 'resolved',
      resolvedAt: '2026-03-04T13:30:00.000Z',
      resolvedBy: 'agent',
      resolutionTime: 3303211,
    },
  },
  'ticket.closed': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440004',
    type: 'ticket.closed',
    ...ENVELOPE,
    created_at: '2026-03-04T14:00:00.000Z',
    data: {
      ...TICKET_REF,
      previousStatus: 'resolved',
      newStatus: 'closed',
      closedAt: '2026-03-04T14:00:00.000Z',
    },
  },
  'ticket.reopened': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440014',
    type: 'ticket.reopened',
    ...ENVELOPE,
    created_at: '2026-03-05T09:00:00.000Z',
    data: {
      ...TICKET_REF,
      previousStatus: 'resolved',
      newStatus: 'OPEN',
      reopenedAt: '2026-03-05T09:00:00.000Z',
      reason: 'Customer replied',
    },
  },
  'ticket.message_added': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440015',
    type: 'ticket.message_added',
    ...ENVELOPE,
    created_at: '2026-03-04T12:40:00.000Z',
    data: {
      ticketId: '660e8400-e29b-41d4-a716-446655440001',
      messageId: 'cc0e8400-e29b-41d4-a716-446655440007',
      conversationId: 'dd0e8400-e29b-41d4-a716-446655440008',
      customerId: '770e8400-e29b-41d4-a716-446655440002',
      senderType: 'agent',
      senderName: 'Sam Agent',
      channel: 'email',
      isInternal: false,
      aiGenerated: false,
      content: '<p>I would be happy to help you with that. A replacement ships today.</p>',
      contentType: 'html',
      contentTruncated: false,
      contentPreview: '<p>I would be happy to help you with that. A replacement ships today.</p>',
      attachments: [
        {
          fileId: 'ab0e8400-e29b-41d4-a716-446655440011',
          name: 'shipping-label.pdf',
          mimeType: 'application/pdf',
          size: 48213,
        },
      ],
      createdAt: '2026-03-04T12:40:00.000Z',
    },
  },
  'ticket.note_added': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440018',
    type: 'ticket.note_added',
    ...ENVELOPE,
    created_at: '2026-03-04T12:42:00.000Z',
    data: {
      ticketId: '660e8400-e29b-41d4-a716-446655440001',
      messageId: 'cc0e8400-e29b-41d4-a716-446655440012',
      conversationId: 'dd0e8400-e29b-41d4-a716-446655440008',
      customerId: '770e8400-e29b-41d4-a716-446655440002',
      senderType: 'agent',
      senderName: 'Sam Agent',
      channel: 'email',
      isInternal: true,
      aiGenerated: false,
      content: 'Refund approved by finance; waiting on the courier.',
      contentType: 'text',
      contentTruncated: false,
      contentPreview: 'Refund approved by finance; waiting on the courier.',
      attachments: [],
      createdAt: '2026-03-04T12:42:00.000Z',
    },
  },
  'customer.created': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440006',
    type: 'customer.created',
    ...ENVELOPE,
    created_at: '2026-03-04T10:00:00.000Z',
    data: {
      ...EXAMPLE_CUSTOMER,
      name: 'Jane Doe',
    },
  },
  'customer.updated': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440007',
    type: 'customer.updated',
    ...ENVELOPE,
    created_at: '2026-03-04T11:00:00.000Z',
    data: {
      ...EXAMPLE_CUSTOMER,
      changes: { name: { old: 'Jane Doe', new: 'Jane Smith' } },
    },
  },
  'customer.deleted': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440008',
    type: 'customer.deleted',
    ...ENVELOPE,
    created_at: '2026-03-04T12:00:00.000Z',
    data: {
      ...EXAMPLE_CUSTOMER,
      reason: 'merged',
      mergedIntoCustomerId: '770e8400-e29b-41d4-a716-446655440009',
      mergedIntoExternalId: 'CRM-2240',
    },
  },
  'kb_article.changed': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440016',
    type: 'kb_article.changed',
    ...ENVELOPE,
    created_at: '2026-03-04T15:00:00.000Z',
    data: {
      action: 'updated',
      resource: { id: 'ee0e8400-e29b-41d4-a716-446655440009', title: 'Resetting your password' },
      changes: { title: { from: 'Reset password', to: 'Resetting your password' } },
    },
  },
  'kb_category.changed': {
    id: 'evt_550e8400-e29b-41d4-a716-446655440017',
    type: 'kb_category.changed',
    ...ENVELOPE,
    created_at: '2026-03-04T15:05:00.000Z',
    data: {
      action: 'created',
      resource: { id: 'ff0e8400-e29b-41d4-a716-446655440010', name: 'Billing' },
      changes: null,
    },
  },
};

/** The server's error message for a failed request, or `fallback`. */
function errorMessage(error: any, fallback: string): string {
  const message = error?.response?.data?.message;
  if (Array.isArray(message)) return message.join('; ');
  return typeof message === 'string' && message ? message : fallback;
}

// A signing secret, shown once (after create or rotate) with a copy button.
function SecretRevealModal({
  title,
  secret,
  note,
  onClose,
}: {
  title: string;
  secret: string;
  note?: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-800 text-sm dark:bg-amber-900/30 dark:text-amber-200">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Copy this signing secret now. It is shown only once and cannot be read back later.</span>
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 px-3 py-2 text-xs break-all bg-gray-100 rounded-lg dark:bg-gray-900 dark:text-gray-200">
              {secret}
            </code>
            <button
              type="button"
              onClick={copy}
              className="p-2 text-gray-500 border rounded-lg hover:bg-gray-50 dark:text-gray-300 dark:border-gray-700"
              title="Copy secret"
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          {note && <p className="text-xs text-gray-500 dark:text-gray-400">{note}</p>}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
            >
              I've saved it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Status badge component
function DeliveryStatusBadge({ status }: { status: DeliveryStatus | string }) {
  // Normalize status to lowercase (backend Prisma uses UPPERCASE, frontend uses lowercase)
  const normalizedStatus = (String(status).toLowerCase()) as DeliveryStatus;

  const styles: Record<string, string> = {
    [DeliveryStatus.SUCCESS]: 'bg-green-100 text-green-700',
    [DeliveryStatus.FAILED]: 'bg-red-100 text-red-700',
    [DeliveryStatus.PENDING]: 'bg-yellow-100 text-yellow-700',
    retrying: 'bg-yellow-100 text-yellow-700',
  };

  const icons: Record<string, any> = {
    [DeliveryStatus.SUCCESS]: CheckCircle,
    [DeliveryStatus.FAILED]: XCircle,
    [DeliveryStatus.PENDING]: Clock,
    retrying: RefreshCw,
  };

  const Icon = icons[normalizedStatus] || AlertCircle;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[normalizedStatus] || 'bg-gray-100 text-gray-700'} dark:bg-gray-800 dark:text-gray-200`}>
      <Icon className="h-3 w-3" />
      {normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)}
    </span>
  );
}

// Webhook Form Modal
function WebhookFormModal({
  webhook,
  eventTypes,
  onClose,
  onSave,
  isSaving,
}: {
  webhook: WebhookType | null;
  eventTypes: WebhookEventType[];
  onClose: () => void;
  onSave: (data: CreateWebhookDto | UpdateWebhookDto) => void;
  isSaving: boolean;
}) {
  // Events the catalog does not know (e.g. `chat.started` from the old list)
  // are never sent; the form drops them and says so.
  const [{ known: initialEvents, unknown: droppedEvents }] = useState(() =>
    partitionWebhookEvents(webhook?.events || [], eventTypes),
  );
  const [name, setName] = useState(webhook?.name || '');
  const [url, setUrl] = useState(webhook?.url || '');
  const [secret, setSecret] = useState('');
  const [events, setEvents] = useState<WebhookEvent[]>(initialEvents);
  const [headers, setHeaders] = useState<{ key: string; value: string }[]>(
    webhook?.headers
      ? Object.entries(webhook.headers).map(([key, value]) => ({ key, value }))
      : [{ key: '', value: '' }]
  );
  const [isEnabled, setIsEnabled] = useState(webhook?.isEnabled ?? true);
  const [showExamplePayloads, setShowExamplePayloads] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<{
    reachable: boolean;
    statusCode?: number;
    message: string;
  } | null>(null);

  const handleVerifyUrl = async () => {
    if (!url) return;
    setIsVerifying(true);
    setVerifyResult(null);
    try {
      const result = await integrationService.verifyWebhookUrl(url);
      setVerifyResult(result);
    } catch {
      setVerifyResult({ reachable: false, message: 'Verification request failed' });
    } finally {
      setIsVerifying(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // The API refuses an empty `events` list (it would receive nothing); the
    // Save button is already disabled, this covers any other submit path.
    if (events.length === 0) return;

    const headersObj: Record<string, string> = {};
    headers.forEach(({ key, value }) => {
      if (key.trim()) headersObj[key.trim()] = value;
    });

    const data = {
      name,
      url,
      events,
      headers: Object.keys(headersObj).length > 0 ? headersObj : {},
      isEnabled,
      ...(secret && { secret }),
    };

    onSave(data);
  };

  const toggleEvent = (event: WebhookEvent) => {
    setEvents((prev) =>
      prev.includes(event) ? prev.filter((e) => e !== event) : [...prev, event]
    );
  };

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index: number) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const updateHeader = (index: number, field: 'key' | 'value', value: string) => {
    const updated = [...headers];
    updated[index][field] = value;
    setHeaders(updated);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {webhook ? 'Edit Webhook' : 'Create Webhook'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-500">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:border-gray-700"
                placeholder="My Webhook"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
                Endpoint URL *
              </label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => {
                    setUrl(e.target.value);
                    setVerifyResult(null);
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm dark:border-gray-700"
                  placeholder="https://example.com/webhook"
                  required
                />
                <button
                  type="button"
                  onClick={handleVerifyUrl}
                  disabled={!url || isVerifying}
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 whitespace-nowrap flex items-center gap-1 dark:border-gray-700"
                  title="Verify URL is reachable"
                >
                  {isVerifying ? (
                    <RefreshCw className="h-4 w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-4 w-4" />
                  )}
                  {isVerifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
              {verifyResult && (
                <div className={`mt-1 flex items-center gap-1 text-xs ${
                  verifyResult.reachable ? 'text-green-600' : 'text-red-600'
                }`}>
                  {verifyResult.reachable ? (
                    <CheckCircle className="h-3 w-3" />
                  ) : (
                    <XCircle className="h-3 w-3" />
                  )}
                  {verifyResult.message}
                </div>
              )}
            </div>
          </div>

          {/* Secret */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-200">
              Secret Key {webhook ? '(leave blank to keep existing)' : '(optional)'}
            </label>
            <PasswordInput
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm dark:border-gray-700"
              placeholder={
                webhook
                  ? 'Enter a new secret, or use Rotate secret on the list'
                  : 'Leave blank to generate a secure secret'
              }
            />
            <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
              Used to sign webhook payloads. Verify the X-Webhook-Signature-V2 header (signed with
              X-Webhook-Timestamp-Unix); the legacy X-Webhook-Signature header is also sent.
              {!webhook && ' Left blank, a secret is generated and shown to you once.'}
            </p>
          </div>

          {/* Events */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-200">
              Events *
            </label>
            <div className="grid grid-cols-2 gap-2">
              {eventTypes.map(({ type, label, description }) => (
                <label
                  key={type}
                  title={description}
                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-colors ${
                    events.includes(type)
                      ? 'bg-primary-50 border-primary-300'
                      : 'bg-gray-50 border-gray-200 hover:bg-gray-100 dark:bg-gray-900 dark:border-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={events.includes(type)}
                    onChange={() => toggleEvent(type)}
                    className="rounded text-primary-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-200">{label}</span>
                </label>
              ))}
            </div>
            {droppedEvents.length > 0 && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                Removed events that are never sent: {droppedEvents.join(', ')}. Saving updates the
                subscription.
              </p>
            )}
            {events.length === 0 && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Select at least one event. A webhook only receives the events it lists.
              </p>
            )}

            {/* Example Payloads (collapsible) */}
            {events.length > 0 && (
              <div className="mt-3">
                <button
                  type="button"
                  onClick={() => setShowExamplePayloads(!showExamplePayloads)}
                  className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  {showExamplePayloads ? (
                    <ChevronDown className="h-3 w-3" />
                  ) : (
                    <ChevronRight className="h-3 w-3" />
                  )}
                  {showExamplePayloads ? 'Hide' : 'Show'} Example Payloads
                </button>
                {showExamplePayloads && (
                  <div className="mt-2 space-y-3 max-h-80 overflow-y-auto">
                    {events.filter((event) => examplePayloads[event]).map((event) => (
                      <div key={event} className="rounded border border-gray-200 dark:border-gray-700">
                        <div className="px-3 py-2 text-xs font-medium text-gray-600 border-b bg-gray-100 rounded-t dark:bg-gray-800 dark:text-gray-400">
                          {integrationService.getWebhookEventLabel(event, eventTypes)}
                        </div>
                        <pre className="p-3 text-xs text-gray-700 overflow-x-auto bg-gray-50 rounded-b dark:bg-gray-900 dark:text-gray-200">
                          {JSON.stringify(examplePayloads[event], null, 2)}
                        </pre>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Custom Headers */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Custom Headers (optional)
              </label>
              <button
                type="button"
                onClick={addHeader}
                className="text-sm text-primary-600 hover:text-primary-700"
              >
                + Add Header
              </button>
            </div>
            <div className="space-y-2">
              {headers.map((header, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={header.key}
                    onChange={(e) => updateHeader(index, 'key', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm dark:border-gray-700"
                    placeholder="Header Name"
                  />
                  <input
                    type="text"
                    value={header.value}
                    onChange={(e) => updateHeader(index, 'value', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm dark:border-gray-700"
                    placeholder="Header Value"
                  />
                  <button
                    type="button"
                    onClick={() => removeHeader(index)}
                    className="p-2 text-gray-400 hover:text-red-600 dark:text-gray-500"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Enabled Toggle */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setIsEnabled(!isEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                isEnabled ? 'bg-primary-600' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                  isEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm text-gray-700 dark:text-gray-200">
              {isEnabled ? 'Webhook is enabled' : 'Webhook is disabled'}
            </span>
          </div>

          {/* Actions — pinned to the foot of the scrolling form, so Cancel and
              Save are in view however tall the form is. */}
          <div className="sticky bottom-0 z-[1] -mx-6 !-mb-6 flex justify-end gap-3 border-t bg-white px-6 pb-6 pt-4 dark:bg-gray-800">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !name || !url || events.length === 0}
              className="px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : webhook ? 'Update Webhook' : 'Create Webhook'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Delivery Logs Panel
function DeliveryLogsPanel({
  webhook,
  eventTypes,
  onClose,
}: {
  webhook: WebhookType;
  eventTypes: WebhookEventType[];
  onClose: () => void;
}) {
  const [deliveries, setDeliveries] = useState<WebhookDelivery[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    loadDeliveries();
  }, [webhook.id]);

  const loadDeliveries = async () => {
    setIsLoading(true);
    try {
      const data = await integrationService.getWebhookDeliveries(webhook.id);
      setDeliveries(data);
    } catch {
      toast.error('Failed to load delivery logs');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async (deliveryId: string) => {
    try {
      await integrationService.retryDelivery(deliveryId);
      toast.success('Retry queued');
      loadDeliveries();
    } catch {
      toast.error('Failed to retry delivery');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden dark:bg-gray-800">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Delivery Logs</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{webhook.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadDeliveries}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:text-gray-500"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:text-gray-500">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
            </div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <Send className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No deliveries yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {deliveries.map((delivery) => (
                <div
                  key={delivery.id}
                  className="border rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedId(expandedId === delivery.id ? null : delivery.id)}
                  >
                    <div className="flex items-center gap-4">
                      <DeliveryStatusBadge status={delivery.status} />
                      <span className="text-sm text-gray-700 dark:text-gray-200">
                        {integrationService.getWebhookEventLabel(delivery.event, eventTypes)}
                      </span>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(delivery.createdAt).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      {String(delivery.status).toLowerCase() === DeliveryStatus.FAILED && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRetry(delivery.id);
                          }}
                          className="p-1 text-gray-400 hover:text-primary-600 dark:text-gray-500"
                          title="Retry"
                        >
                          <RotateCcw className="h-4 w-4" />
                        </button>
                      )}
                      {expandedId === delivery.id ? (
                        <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      )}
                    </div>
                  </div>

                  {expandedId === delivery.id && (
                    <div className="px-4 pb-4 border-t bg-gray-50 dark:bg-gray-900">
                      <div className="grid grid-cols-2 gap-4 mt-3">
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-1 dark:text-gray-400">Request Payload</h4>
                          <pre className="text-xs bg-white p-3 rounded border overflow-auto max-h-48 dark:bg-gray-800">
                            {JSON.stringify(delivery.payload, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <h4 className="text-xs font-medium text-gray-500 mb-1 dark:text-gray-400">Response</h4>
                          <div className="text-xs bg-white p-3 rounded border dark:bg-gray-800">
                            <p><span className="text-gray-500 dark:text-gray-400">Status:</span> {delivery.statusCode || 'N/A'}</p>
                            <p><span className="text-gray-500 dark:text-gray-400">Attempts:</span> {delivery.attempts}</p>
                            {delivery.deliveredAt && (
                              <p><span className="text-gray-500 dark:text-gray-400">Delivered:</span> {new Date(delivery.deliveredAt).toLocaleString()}</p>
                            )}
                            {delivery.nextRetryAt && (
                              <p><span className="text-gray-500 dark:text-gray-400">Next retry:</span> {new Date(delivery.nextRetryAt).toLocaleString()}</p>
                            )}
                            {delivery.responseBody && (
                              <pre className="mt-2 overflow-auto max-h-24">{delivery.responseBody}</pre>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Main Component
export default function WebhookManagement() {
  const [webhooks, setWebhooks] = useState<WebhookType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [selectedWebhook, setSelectedWebhook] = useState<WebhookType | null>(null);
  const [logsWebhook, setLogsWebhook] = useState<WebhookType | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [eventTypes, setEventTypes] = useState<WebhookEventType[]>(FALLBACK_WEBHOOK_EVENT_TYPES);
  // A signing secret to show once: after a create that generated one, or a rotation.
  const [revealedSecret, setRevealedSecret] = useState<{
    title: string;
    secret: string;
    note?: string;
  } | null>(null);
  const [rotatingId, setRotatingId] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    loadWebhooks();
    loadEventTypes();
  }, []);

  // The hub's catalog is the list to offer; the built-in copy only covers a
  // failed request.
  const loadEventTypes = async () => {
    try {
      const types = await integrationService.getWebhookEventTypes();
      if (Array.isArray(types) && types.length > 0) setEventTypes(types);
    } catch {
      // Keep FALLBACK_WEBHOOK_EVENT_TYPES.
    }
  };

  const loadWebhooks = async () => {
    setIsLoading(true);
    try {
      const data = await integrationService.getWebhooks();
      setWebhooks(data);
    } catch {
      toast.error('Failed to load webhooks');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedWebhook(null);
    setShowFormModal(true);
  };

  const handleEdit = (webhook: WebhookType) => {
    setSelectedWebhook(webhook);
    setShowFormModal(true);
  };

  const handleSave = async (data: CreateWebhookDto | UpdateWebhookDto) => {
    setIsSaving(true);
    try {
      if (selectedWebhook) {
        await integrationService.updateWebhook(selectedWebhook.id, data);
        toast.success('Webhook updated');
      } else {
        const created = await integrationService.createWebhook(data as CreateWebhookDto);
        toast.success('Webhook created');
        if (created?.secret) {
          setRevealedSecret({
            title: 'Webhook signing secret',
            secret: created.secret,
            note: 'Use it to verify the X-Webhook-Signature-V2 header on every delivery.',
          });
        }
      }
      setShowFormModal(false);
      loadWebhooks();
    } catch (error) {
      // The server says why (e.g. a URL that is not https or not public).
      toast.error(
        errorMessage(
          error,
          selectedWebhook ? 'Failed to update webhook' : 'Failed to create webhook',
        ),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleRotateSecret = async (webhook: WebhookType) => {
    if (
      !confirm(
        `Rotate the signing secret for "${webhook.name}"?\n\n` +
          'A new secret is generated and shown once. For the next 24 hours deliveries are ' +
          'signed with both the new and the old secret, so you can update your receiver ' +
          'without missing events.',
      )
    ) {
      return;
    }
    setRotatingId(webhook.id);
    try {
      const rotated = await integrationService.rotateWebhookSecret(webhook.id);
      setRevealedSecret({
        title: `New signing secret for "${webhook.name}"`,
        secret: rotated.secret,
        note: `The previous secret keeps signing until ${new Date(rotated.expiresAt).toLocaleString()}.`,
      });
      loadWebhooks();
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to rotate the secret'));
    } finally {
      setRotatingId(null);
    }
  };

  const handleDelete = async (webhook: WebhookType) => {
    if (!confirm(`Delete webhook "${webhook.name}"? This action cannot be undone.`)) return;
    try {
      await integrationService.deleteWebhook(webhook.id);
      toast.success('Webhook deleted');
      loadWebhooks();
    } catch {
      toast.error('Failed to delete webhook');
    }
  };

  const handleToggleEnabled = async (webhook: WebhookType) => {
    try {
      await integrationService.updateWebhook(webhook.id, { isEnabled: !webhook.isEnabled });
      toast.success(webhook.isEnabled ? 'Webhook disabled' : 'Webhook enabled');
      loadWebhooks();
    } catch {
      toast.error('Failed to update webhook');
    }
  };

  const handleTest = async (webhook: WebhookType) => {
    try {
      const result = await integrationService.testWebhook(webhook.id);
      if (result.success) {
        toast.success('Test successful');
      } else {
        toast.error(`Test failed: ${result.message}`);
      }
    } catch {
      toast.error('Failed to send test');
    }
  };

  const copyUrl = async (url: string, id: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredWebhooks = webhooks.filter((w) =>
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.url.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Signed with the placeholder the old form stored when the secret was left
  // blank: anyone can forge their signatures until they are rotated.
  const insecureWebhooks = webhooks.filter((w) => w.usesDefaultSecret);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Webhooks</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Configure webhooks to receive real-time event notifications
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Create Webhook
        </button>
      </div>

      {insecureWebhooks.length > 0 && (
        <div
          role="alert"
          className="flex items-start gap-3 p-4 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
        >
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">
              {insecureWebhooks.length === 1
                ? '1 webhook uses a publicly known signing secret'
                : `${insecureWebhooks.length} webhooks use a publicly known signing secret`}
            </p>
            <p className="mt-1">
              They were created without a secret, so their signatures can be forged. Use{' '}
              <strong>Rotate secret</strong> on {insecureWebhooks.map((w) => `"${w.name}"`).join(', ')}{' '}
              and update your receiver with the new secret.
            </p>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search webhooks..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm dark:border-gray-700"
        />
      </div>

      {/* List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-6 w-6 animate-spin text-gray-400 dark:text-gray-500" />
        </div>
      ) : filteredWebhooks.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border dark:bg-gray-800">
          <Webhook className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p className="text-gray-500 dark:text-gray-400">
            {searchQuery ? 'No webhooks found' : 'No webhooks configured'}
          </p>
          {!searchQuery && (
            <button
              onClick={handleCreate}
              className="mt-4 text-sm text-primary-600 hover:text-primary-700"
            >
              Create your first webhook
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredWebhooks.map((webhook) => (
            <div
              key={webhook.id}
              className="bg-white rounded-lg border p-4 hover:shadow-sm transition-shadow dark:bg-gray-800"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">{webhook.name}</h3>
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                        webhook.isEnabled
                          ? 'bg-green-100 text-green-700'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                      }`}
                    >
                      {webhook.isEnabled ? 'Active' : 'Disabled'}
                    </span>
                    {webhook.usesDefaultSecret && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        <AlertCircle className="h-3 w-3" />
                        Insecure secret
                      </span>
                    )}
                    {webhook.secretRotationEndsAt && (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200"
                        title="Deliveries are signed with both the new and the previous secret until then"
                      >
                        <Clock className="h-3 w-3" />
                        Previous secret valid until{' '}
                        {new Date(webhook.secretRotationEndsAt).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-sm text-gray-500 truncate max-w-md dark:text-gray-400">
                      {webhook.url}
                    </span>
                    <button
                      onClick={() => copyUrl(webhook.url, webhook.id)}
                      className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-500"
                      title="Copy URL"
                    >
                      {copiedId === webhook.id ? (
                        <Check className="h-3 w-3 text-green-600" />
                      ) : (
                        <Copy className="h-3 w-3" />
                      )}
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {webhook.events.slice(0, 3).map((event) => (
                      <span
                        key={event}
                        className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded dark:bg-gray-800 dark:text-gray-400"
                      >
                        {integrationService.getWebhookEventLabel(event, eventTypes)}
                      </span>
                    ))}
                    {webhook.events.length > 3 && (
                      <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-500 rounded dark:bg-gray-800 dark:text-gray-400">
                        +{webhook.events.length - 3} more
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setLogsWebhook(webhook)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:text-gray-500"
                    title="View Logs"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleTest(webhook)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:text-gray-500"
                    title="Send Test"
                  >
                    <Send className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleToggleEnabled(webhook)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:text-gray-500"
                    title={webhook.isEnabled ? 'Disable' : 'Enable'}
                  >
                    {webhook.isEnabled ? (
                      <PowerOff className="h-4 w-4" />
                    ) : (
                      <Power className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleRotateSecret(webhook)}
                    disabled={rotatingId === webhook.id}
                    className={`p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 ${
                      webhook.usesDefaultSecret
                        ? 'text-amber-600 hover:text-amber-700'
                        : 'text-gray-400 hover:text-gray-600 dark:text-gray-500'
                    }`}
                    title="Rotate secret"
                    aria-label={`Rotate secret for ${webhook.name}`}
                  >
                    {rotatingId === webhook.id ? (
                      <RefreshCw className="h-4 w-4 animate-spin" />
                    ) : (
                      <KeyRound className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => handleEdit(webhook)}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 dark:text-gray-500"
                    title="Edit"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(webhook)}
                    className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100 dark:text-gray-500"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showFormModal && (
        <WebhookFormModal
          webhook={selectedWebhook}
          eventTypes={eventTypes}
          onClose={() => setShowFormModal(false)}
          onSave={handleSave}
          isSaving={isSaving}
        />
      )}

      {/* Logs Panel */}
      {logsWebhook && (
        <DeliveryLogsPanel
          webhook={logsWebhook}
          eventTypes={eventTypes}
          onClose={() => setLogsWebhook(null)}
        />
      )}

      {revealedSecret && (
        <SecretRevealModal
          title={revealedSecret.title}
          secret={revealedSecret.secret}
          note={revealedSecret.note}
          onClose={() => setRevealedSecret(null)}
        />
      )}
    </div>
  );
}
